import { useState } from 'react';
import type {
  ComparisonLanguageChanges,
  NoticeComparison,
} from '@dpdp/sdk';
import { DEFAULT_PURPOSES } from '../../lib/constants';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { compareNoticeVersions, getNetworkErrorMessage } from '../../lib/api';
import type { MultilingualNotice, NoticeVersionSummary, Purpose } from '../../lib/types';

interface ConsentNoticeDesignerProps {
  notices: MultilingualNotice | null;
  isLoading: boolean;
  onPublish: (data: { enTitle: string; enPurposes: Purpose[]; hiTitle: string; hiPurposes: Purpose[] }) => Promise<void>;
  isPublishing: boolean;
  onRollback: (noticeId: string, fromVersion: number) => Promise<void>;
  isRollingBack: boolean;
  noticeVersions: NoticeVersionSummary[];
  noticeVersionsLoading: boolean;
  noticeVersionsError: string | null;
  applicationId: string;
}

export function ConsentNoticeDesigner({
  notices,
  isLoading,
  onPublish,
  isPublishing,
  onRollback,
  isRollingBack,
  noticeVersions,
  noticeVersionsLoading,
  noticeVersionsError,
}: ConsentNoticeDesignerProps) {
  const { addToast } = useToast();
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  const [enTitle, setEnTitle] = useState(notices?.en?.title || 'Data Privacy Notice');
  const [enPurposes, setEnPurposes] = useState<Purpose[]>(notices?.en?.purposes || [...DEFAULT_PURPOSES.en]);
  const [hiTitle, setHiTitle] = useState(notices?.hi?.title || 'सहमति सूचना');
  const [hiPurposes, setHiPurposes] = useState<Purpose[]>(notices?.hi?.purposes || [...DEFAULT_PURPOSES.hi]);

  // Version comparison state
  // selection: id of the row picked as "Version A" and "Version B"
  const [versionAId, setVersionAId] = useState<string | null>(null);
  const [versionBId, setVersionBId] = useState<string | null>(null);
  const [comparison, setComparison] = useState<NoticeComparison | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  const canCompare =
    versionAId !== null && versionBId !== null && versionAId !== versionBId;

  const handleCompare = async () => {
    if (!versionAId || !versionBId) return;
    setComparisonLoading(true);
    setComparisonError(null);
    setComparison(null);
    setShowComparison(true);
    try {
      const result = await compareNoticeVersions(versionAId, versionBId);
      setComparison(result);
    } catch (e) {
      setComparisonError(getNetworkErrorMessage(e, 'Failed to load version comparison.'));
    } finally {
      setComparisonLoading(false);
    }
  };

  const closeComparison = () => {
    setShowComparison(false);
    setComparison(null);
    setComparisonError(null);
  };

  // Resolve a version number label (e.g. "v3") from a selected notice id.
  const versionLabelFor = (id: string | null): string => {
    if (!id) return '—';
    const found = noticeVersions.find((nv) => nv.id === id);
    return found ? `v${found.version}` : 'unknown';
  };

  // Rollback state — the historical version the user intends to restore.
  const [rollbackTarget, setRollbackTarget] = useState<NoticeVersionSummary | null>(null);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  // The currently live version (used to show "from" in the confirmation modal).
  const liveVersion = noticeVersions.find((nv) => nv.isActive) ?? null;

  const openRollback = (version: NoticeVersionSummary) => {
    setRollbackTarget(version);
    setRollbackError(null);
  };

  const closeRollback = () => {
    setRollbackTarget(null);
    setRollbackError(null);
  };

  const handleConfirmRollback = async () => {
    if (!rollbackTarget) return;
    setRollbackError(null);
    try {
      await onRollback(rollbackTarget.id, rollbackTarget.version);
      closeRollback();
    } catch (e) {
      setRollbackError(getNetworkErrorMessage(e, 'Failed to roll back notice.'));
    }
  };

  // Validation
  const validate = (): { valid: true } | { valid: false; message: string } => {
    if (!enTitle.trim()) return { valid: false, message: 'English title is required.' };
    if (!hiTitle.trim()) return { valid: false, message: 'Hindi title is required.' };
    for (const p of enPurposes) {
      if (!p.title.trim()) return { valid: false, message: 'All English purposes must have a title.' };
      if (!p.description.trim()) return { valid: false, message: 'All English purposes must have a description.' };
    }
    for (const p of hiPurposes) {
      if (!p.title.trim()) return { valid: false, message: 'All Hindi purposes must have a title.' };
      if (!p.description.trim()) return { valid: false, message: 'All Hindi purposes must have a description.' };
    }
    return { valid: true };
  };

  const handlePublishClick = () => {
    const validation = validate();
    if (!validation.valid) {
      addToast({ type: 'error', message: validation.message });
      return;
    }
    setShowPublishConfirm(true);
  };

  const handleConfirmPublish = async () => {
    try {
      await onPublish({ enTitle, enPurposes, hiTitle, hiPurposes });
      setShowPublishConfirm(false);
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, 'Failed to publish notice.') });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-extrabold text-white">Consent Notice Designer</h2>
          <p className="mt-1 text-sm text-slate-400">
            Create the privacy notice your users will review before giving consent.
          </p>
        </div>
        <button
          onClick={handlePublishClick}
          disabled={isPublishing}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 transition"
        >
          {isPublishing ? (
            <>
              <Spinner size="sm" /> Publishing...
            </>
          ) : (
            'Publish Draft'
          )}
        </button>
      </div>

      {/* Introduction Card */}
      <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-6 space-y-2">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          📜 Consent Notices
        </h3>
        <p className="text-sm text-slate-300 leading-relaxed">
          Every published notice becomes an immutable version that remains linked to previously collected consents.
          Draft changes do not affect your live consent notices until you publish them.
        </p>
      </div>

      {/* Version History Panel */}
      <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-blue-400"></span>
          Version History
        </h3>

        {noticeVersionsLoading && (
          <div className="flex items-center gap-3 py-8 justify-center">
            <Spinner size="sm" />
            <span className="text-xs text-slate-400">Loading version history...</span>
          </div>
        )}

        {noticeVersionsError && !noticeVersionsLoading && (
          <div className="rounded-xl border border-red-800/50 bg-red-950/20 p-4 text-xs text-red-300">
            <p>{noticeVersionsError}</p>
          </div>
        )}

        {!noticeVersionsLoading && !noticeVersionsError && noticeVersions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-3xl text-slate-500 mb-2">📋</div>
            <p className="text-sm text-slate-400">No versions published yet.</p>
            <p className="text-xs text-slate-500 mt-1">
              Publish your first notice to start tracking version history.
            </p>
          </div>
        )}

        {!noticeVersionsLoading && noticeVersions.length > 0 && (
          <div className="space-y-4">
            <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-300">
              <thead className="border-b border-slate-900 bg-slate-950 text-xs font-bold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Published Date</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-900">
                {noticeVersions.map((nv) => (
                  <tr key={nv.id} className="hover:bg-slate-900/30">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-white">
                      v{nv.version}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          nv.isActive
                            ? 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
                            : 'border border-slate-700/30 bg-slate-800/30 text-slate-400'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            nv.isActive ? 'bg-emerald-400' : 'bg-slate-500'
                          }`}
                        ></span>
                        {nv.isActive ? 'Live' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {new Date(nv.createdAt * 1000).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end" role="group" aria-label={`Actions for version ${nv.version}`}>
                        <button
                          type="button"
                          onClick={() => {
                            // If this row already holds B, move it to A; otherwise just assign A.
                            if (versionBId === nv.id) {
                              setVersionBId(versionAId);
                            }
                            setVersionAId(nv.id);
                          }}
                          aria-pressed={versionAId === nv.id}
                          aria-label={`Use version ${nv.version} as Version A`}
                          className={`rounded-md px-2 py-1 text-[10px] font-bold tracking-wide transition ${
                            versionAId === nv.id
                              ? 'border border-blue-500/40 bg-blue-500/20 text-blue-300'
                              : 'border border-slate-800 bg-slate-950 text-slate-500 hover:text-slate-300 hover:border-slate-700'
                          }`}
                        >
                          A
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (versionAId === nv.id) {
                              setVersionAId(versionBId);
                            }
                            setVersionBId(nv.id);
                          }}
                          aria-pressed={versionBId === nv.id}
                          aria-label={`Use version ${nv.version} as Version B`}
                          className={`rounded-md px-2 py-1 text-[10px] font-bold tracking-wide transition ${
                            versionBId === nv.id
                              ? 'border border-indigo-500/40 bg-indigo-500/20 text-indigo-300'
                              : 'border border-slate-800 bg-slate-950 text-slate-500 hover:text-slate-300 hover:border-slate-700'
                          }`}
                        >
                          B
                        </button>
                        {/* Rollback — only available for historical (non-active) versions */}
                        {!nv.isActive && (
                          <button
                            type="button"
                            onClick={() => openRollback(nv)}
                            disabled={isRollingBack}
                            aria-label={`Roll back to version ${nv.version}`}
                            className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-bold tracking-wide text-amber-400 hover:bg-amber-500/20 hover:border-amber-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                          >
                            ↩ Rollback
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Compare trigger: shown once at least one version is picked */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/50 p-4">
              <div className="text-xs text-slate-400 leading-relaxed">
                {canCompare ? (
                  <span>
                    Comparing{' '}
                    <span className="font-mono font-semibold text-blue-300">
                      {versionLabelFor(versionAId)}
                    </span>{' '}
                    with{' '}
                    <span className="font-mono font-semibold text-indigo-300">
                      {versionLabelFor(versionBId)}
                    </span>
                    .
                  </span>
                ) : versionAId || versionBId ? (
                  <span>Pick a second version to compare against.</span>
                ) : (
                  <span>Select two versions using the A / B buttons to compare them.</span>
                )}
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                {(versionAId || versionBId) && (
                  <button
                    type="button"
                    onClick={() => {
                      setVersionAId(null);
                      setVersionBId(null);
                    }}
                    className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-xs font-semibold text-slate-400 hover:text-slate-200 hover:border-slate-700 transition"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCompare}
                  disabled={!canCompare}
                  className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow hover:from-blue-500 hover:to-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  ⚖️ Compare Versions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {notices ? (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column: Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Version Status Card */}
            <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
                    Current Published Notice
                  </h4>
                  <div className="space-y-1 text-xs text-slate-400">
                    <div className="flex justify-between">
                      <span>Version</span>
                      <span className="font-mono font-semibold text-white">{notices.version || 1}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status</span>
                      <span className="text-emerald-400 font-semibold">Live</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-amber-400"></span>
                    Draft Version
                  </h4>
                  <div className="space-y-1 text-xs text-slate-400">
                    <div className="flex justify-between">
                      <span>Status</span>
                      <span className="text-amber-400 font-semibold">Edited</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6">
              <h3 className="text-sm font-bold text-white mb-4">Notice Metadata</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                <div className="bg-slate-950 rounded-lg p-3 border border-slate-900">
                  <span className="text-slate-500 block">Version</span>
                  <span className="text-white font-semibold block mt-1">{notices.version || 1}</span>
                </div>
              </div>
            </div>

            {/* Language Editors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4 rounded-2xl border border-slate-900 bg-slate-900/20 p-6">
                <h3 className="text-lg font-bold text-white">English Notice</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Title
                    </label>
                    <input
                      type="text"
                      value={enTitle}
                      onChange={(e) => setEnTitle(e.target.value)}
                      className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Purpose Matrix
                    </label>
                    {enPurposes.map((p, idx) => (
                      <div
                        key={p.id}
                        className="mt-3 space-y-2 rounded-xl border border-slate-900 bg-slate-950 p-4"
                      >
                        <input
                          type="text"
                          value={p.title}
                          onChange={(e) => {
                            const updated = [...enPurposes];
                            const item = updated[idx];
                            if (item) {
                              item.title = e.target.value;
                              setEnPurposes(updated);
                            }
                          }}
                          className="w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs text-white outline-none"
                          placeholder="Purpose Title"
                        />
                        <textarea
                          value={p.description}
                          onChange={(e) => {
                            const updated = [...enPurposes];
                            const item = updated[idx];
                            if (item) {
                              item.description = e.target.value;
                              setEnPurposes(updated);
                            }
                          }}
                          className="h-16 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs text-slate-400 outline-none"
                          placeholder="Purpose Description"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-slate-900 bg-slate-900/20 p-6">
                <h3 className="text-lg font-bold text-white">Hindi Notice</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      शीर्षक (Title)
                    </label>
                    <input
                      type="text"
                      value={hiTitle}
                      onChange={(e) => setHiTitle(e.target.value)}
                      className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      उद्देश्य (Purposes)
                    </label>
                    {hiPurposes.map((p, idx) => (
                      <div
                        key={p.id}
                        className="mt-3 space-y-2 rounded-xl border border-slate-900 bg-slate-950 p-4"
                      >
                        <input
                          type="text"
                          value={p.title}
                          onChange={(e) => {
                            const updated = [...hiPurposes];
                            const item = updated[idx];
                            if (item) {
                              item.title = e.target.value;
                              setHiPurposes(updated);
                            }
                          }}
                          className="w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs text-white outline-none"
                          placeholder="Purpose Title"
                        />
                        <textarea
                          value={p.description}
                          onChange={(e) => {
                            const updated = [...hiPurposes];
                            const item = updated[idx];
                            if (item) {
                              item.description = e.target.value;
                              setHiPurposes(updated);
                            }
                          }}
                          className="h-16 w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs text-slate-400 outline-none"
                          placeholder="Purpose Description"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Live Preview */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              <h3 className="text-lg font-bold text-white">Live Preview</h3>
              <div className="rounded-2xl border border-slate-900 bg-slate-950 p-6 space-y-6">
                {/* English Preview */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">English</h4>
                  <div className="rounded-xl border border-slate-900 bg-slate-900/50 p-4 space-y-4">
                    <h5 className="text-sm font-semibold text-white">{enTitle}</h5>
                    {enPurposes.map((p) => (
                      <div key={p.id} className="space-y-1 text-xs">
                        <div className="text-slate-200 font-semibold">{p.title}</div>
                        <div className="text-slate-400">{p.description}</div>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <button className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
                        Accept All
                      </button>
                      <button className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-300">
                        Decline All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hindi Preview */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Hindi</h4>
                  <div className="rounded-xl border border-slate-900 bg-slate-900/50 p-4 space-y-4">
                    <h5 className="text-sm font-semibold text-white">{hiTitle}</h5>
                    {hiPurposes.map((p) => (
                      <div key={p.id} className="space-y-1 text-xs">
                        <div className="text-slate-200 font-semibold">{p.title}</div>
                        <div className="text-slate-400">{p.description}</div>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <button className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white">
                        Accept All
                      </button>
                      <button className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-300">
                        Decline All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState
          icon="📜"
          title="No Consent Notice Yet"
          description="Create your first consent notice to start collecting user consents."
          action={
            <button
              onClick={handlePublishClick}
              className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:from-blue-500 hover:to-indigo-500"
            >
              Create Your First Consent Notice
            </button>
          }
        />
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">⚠️</span>
              <div>
                <h3 className="text-xl font-bold text-white">Publish Draft Notice</h3>
                <p className="text-xs text-slate-400 mt-1">
                  This will create a new immutable version.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-xs text-yellow-300 leading-relaxed">
              Publishing creates a permanent notice version that cannot be edited or deleted. All future consents will
              be linked to this new version.
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => setShowPublishConfirm(false)}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-3 text-xs font-semibold text-slate-300 hover:bg-slate-900/60"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPublish}
                disabled={isPublishing}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-xs font-semibold text-white shadow hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isPublishing ? (
                  <>
                    <Spinner size="sm" /> Publishing...
                  </>
                ) : (
                  'Confirm Publish'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version Comparison Modal */}
      {showComparison && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 p-6">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  ⚖️ Version Comparison
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Comparing{' '}
                  <span className="font-mono font-semibold text-blue-300">
                    {versionLabelFor(versionAId)}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono font-semibold text-indigo-300">
                    {versionLabelFor(versionBId)}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeComparison}
                aria-label="Close comparison"
                className="rounded-lg border border-slate-800 bg-slate-900/40 p-2 text-slate-400 hover:text-white hover:border-slate-700 transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body (scrollable) */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {comparisonLoading && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Spinner size="lg" />
                  <p className="text-xs text-slate-400">Loading comparison…</p>
                </div>
              )}

              {!comparisonLoading && comparisonError && (
                <div className="rounded-xl border border-red-800/50 bg-red-950/20 p-4 text-sm text-red-300">
                  <p className="font-semibold mb-1">Could not load comparison</p>
                  <p className="text-xs">{comparisonError}</p>
                </div>
              )}

              {!comparisonLoading && !comparisonError && comparison && (
                <ComparisonResultView comparison={comparison} />
              )}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-slate-800 p-4 flex justify-end">
              <button
                type="button"
                onClick={closeComparison}
                className="rounded-xl border border-slate-800 bg-slate-900/30 px-5 py-2.5 text-xs font-semibold text-slate-300 hover:bg-slate-900/60 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rollback Confirmation Modal */}
      {rollbackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">↩️</span>
              <div>
                <h3 className="text-xl font-bold text-white">Roll Back to v{rollbackTarget.version}</h3>
                <p className="text-xs text-slate-400 mt-1">
                  Restore a previous notice as a new live version.
                </p>
              </div>
            </div>

            {/* How rollback works */}
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-amber-200/90 leading-relaxed space-y-1.5">
              <p className="font-bold flex items-center gap-1.5">⚠️ How rollback works</p>
              <p>This action <span className="font-semibold">does NOT overwrite history</span>.</p>
              <p>A <span className="font-semibold">new notice version</span> will be created using v{rollbackTarget.version} as its source.</p>
              <p>The current published version will become <span className="font-semibold">archived</span>.</p>
              <p>This operation <span className="font-semibold">cannot be undone</span>.</p>
            </div>

            {/* Version summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-800 bg-slate-950 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Current Live Version
                </p>
                <p className="mt-1.5 font-mono text-sm font-semibold text-emerald-400">
                  {liveVersion ? `v${liveVersion.version}` : 'None'}
                </p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Selected Rollback Version
                </p>
                <p className="mt-1.5 font-mono text-sm font-semibold text-blue-400">
                  v{rollbackTarget.version}
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-950 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                New Version Number
              </p>
              <p className="mt-1.5 text-xs text-slate-400">
                Assigned automatically by the server once the rollback completes.
              </p>
            </div>

            {/* Inline error (backend validation) */}
            {rollbackError && (
              <div className="rounded-xl border border-red-800/50 bg-red-950/20 p-4 text-xs text-red-300">
                <p className="font-semibold mb-1">Rollback failed</p>
                <p>{rollbackError}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={closeRollback}
                disabled={isRollingBack}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-3 text-xs font-semibold text-slate-300 hover:bg-slate-900/60 disabled:opacity-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRollback}
                disabled={isRollingBack}
                className="flex-1 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 py-3 text-xs font-semibold text-white shadow hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {isRollingBack ? (
                  <>
                    <Spinner size="sm" /> Rolling back...
                  </>
                ) : (
                  'Confirm Rollback'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison result rendering
// ---------------------------------------------------------------------------

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  hi: 'Hindi (हिन्दी)',
};

/** Determine whether a language has any differences at all. */
function languageHasChanges(lang: ComparisonLanguageChanges): boolean {
  return (
    lang.title.changed ||
    lang.purposes.added.length > 0 ||
    lang.purposes.removed.length > 0 ||
    lang.purposes.modified.length > 0
  );
}

/** Determine whether the whole comparison has any differences. */
function comparisonHasChanges(comparison: NoticeComparison): boolean {
  const metadataChanged =
    comparison.metadata.isActiveA !== comparison.metadata.isActiveB ||
    comparison.metadata.createdAtA !== comparison.metadata.createdAtB;
  const langChanged = Object.values(comparison.languages).some(
    (lang) => lang && languageHasChanges(lang),
  );
  return metadataChanged || langChanged;
}

function ComparisonResultView({ comparison }: { comparison: NoticeComparison }) {
  const hasChanges = comparisonHasChanges(comparison);
  const { metadata, languages } = comparison;

  if (!hasChanges) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-4xl text-slate-500 mb-3">✓</div>
        <p className="text-sm font-semibold text-slate-300">No differences found.</p>
        <p className="text-xs text-slate-500 mt-1">
          These two versions are identical.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metadata changes */}
      <section className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
          Metadata
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MetaRow
            label="Status"
            before={metadata.isActiveA ? 'Live' : 'Archived'}
            after={metadata.isActiveB ? 'Live' : 'Archived'}
            changed={metadata.isActiveA !== metadata.isActiveB}
          />
          <MetaRow
            label="Published"
            before={new Date(metadata.createdAtA * 1000).toLocaleString()}
            after={new Date(metadata.createdAtB * 1000).toLocaleString()}
            changed={metadata.createdAtA !== metadata.createdAtB}
          />
        </div>
      </section>

      {/* Per-language changes */}
      {Object.entries(languages).map(([code, lang]) => {
        if (!lang) return null;
        return (
          <LanguageDiff
            key={code}
            code={code}
            label={LANGUAGE_LABELS[code] ?? code.toUpperCase()}
            lang={lang}
          />
        );
      })}
    </div>
  );
}

function MetaRow({
  label,
  before,
  after,
  changed,
}: {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      {changed ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded bg-red-500/10 px-2 py-0.5 text-red-300 line-through">
            {before}
          </span>
          <span className="text-slate-500">→</span>
          <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-emerald-300">{after}</span>
        </div>
      ) : (
        <p className="mt-1.5 text-xs text-slate-400">{before}</p>
      )}
    </div>
  );
}

function LanguageDiff({
  code,
  label,
  lang,
}: {
  code: string;
  label: string;
  lang: ComparisonLanguageChanges;
}) {
  const hasChanges = languageHasChanges(lang);
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
        {label}{' '}
        <span className="text-[10px] font-mono text-slate-600">({code})</span>
      </h4>

      {!hasChanges && (
        <p className="text-xs text-slate-500">No changes in this language.</p>
      )}

      {hasChanges && (
        <div className="space-y-4">
          {/* Title change */}
          {lang.title.changed && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
                Changed {label} text (title)
              </p>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex gap-2">
                  <span className="text-slate-500 shrink-0">Before:</span>
                  <span className="text-red-300">{lang.title.before || '—'}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-slate-500 shrink-0">After:</span>
                  <span className="text-emerald-300">{lang.title.after || '—'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Added purposes */}
          {lang.purposes.added.length > 0 && (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                Added Purposes ({lang.purposes.added.length})
              </p>
              <ul className="mt-2 space-y-1.5">
                {lang.purposes.added.map((p, i) => (
                  <li key={(p?.id as string) ?? i} className="text-xs text-slate-300">
                    <span className="font-semibold text-emerald-300">+</span>{' '}
                    {p?.title ?? 'Untitled purpose'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Removed purposes */}
          {lang.purposes.removed.length > 0 && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-400">
                Removed Purposes ({lang.purposes.removed.length})
              </p>
              <ul className="mt-2 space-y-1.5">
                {lang.purposes.removed.map((p, i) => (
                  <li key={(p?.id as string) ?? i} className="text-xs text-slate-300">
                    <span className="font-semibold text-red-300">−</span>{' '}
                    {p?.title ?? 'Untitled purpose'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Modified purposes */}
          {lang.purposes.modified.length > 0 && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-blue-400">
                Modified Purposes ({lang.purposes.modified.length})
              </p>
              <ul className="mt-2 space-y-2">
                {lang.purposes.modified.map((p) => (
                  <li key={p.id} className="text-xs space-y-1">
                    <div className="font-semibold text-slate-200">
                      {p.title ?? p.id}
                    </div>
                    {p.title !== undefined && (
                      <ChangeLine label="Title" before={p.before?.title} after={p.after?.title} />
                    )}
                    {p.description !== undefined && (
                      <ChangeLine
                        label="Description"
                        before={p.before?.description}
                        after={p.after?.description}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ChangeLine({
  label,
  before,
  after,
}: {
  label: string;
  before?: string;
  after?: string;
}) {
  if (before === after) return null;
  return (
    <div className="ml-3 flex flex-wrap gap-2 items-baseline">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}:</span>
      <span className="text-red-300 line-through">{before || '—'}</span>
      <span className="text-slate-500">→</span>
      <span className="text-emerald-300">{after || '—'}</span>
    </div>
  );
}
